import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	DownloadReportType,
	IDownloadJobStatus,
	IDownloadJob,
	IDownloadJobsState,
	ICreateReportJobRequestData,
	ICreateReportJobResponseData
} from "../../types";
import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import { queries } from "./queries.reducer";

interface IAddDownloadJobPayload {
	response: ICreateReportJobResponseData;
	reportType: DownloadReportType;
}

interface IUpdateDownloadJobStatusPayload {
	jobId: string;
	status: IDownloadJobStatus;
	progress?: number;
	message?: string;
	error?: string;
}

const initialState: IDownloadJobsState = {
	jobs: []
};

const downloadJobsReducer = createSlice({
	name: "downloadJobs",
	initialState,
	reducers: {
		addDownloadJob: (state, { payload }: PayloadAction<IAddDownloadJobPayload>) => {
			const newJob: IDownloadJob = {
				id: payload.response.job_id,
				jobResponse: payload.response,
				reportType: payload.reportType,
				status: "pending",
				progress: 0,
				message: "",
				error: "",
				createdAt: new Date().toISOString()
			};

			state.jobs.push(newJob);
		},

		updateDownloadJobStatus: (state, { payload }: PayloadAction<IUpdateDownloadJobStatusPayload>) => {
			const jobIndex = state.jobs.findIndex((j) => j.id === payload.jobId);
			if (jobIndex < 0) return;

			const job = state.jobs[jobIndex];
			job.status = payload.status;

			if (payload.progress !== undefined) job.progress = payload.progress;
			if (payload.message !== undefined) job.message = payload.message;
			if (payload.error !== undefined) job.error = payload.error;

			if (payload.status === "completed" || payload.status === "failed") {
				job.completedAt = new Date().toISOString();
			}

			state.jobs[jobIndex] = job;
		},

		removeDownloadJob: (state, { payload }: PayloadAction<string>) => {
			state.jobs = state.jobs.filter((job) => job.id !== payload);
		},

		clearCompletedJobs: (state) => {
			state.jobs = state.jobs.filter((job) => job.status !== "completed" && job.status !== "failed");
		},

		retryDownloadJob: (state, { payload }: PayloadAction<string>) => {
			const job = state.jobs.find((j) => j.id === payload);
			if (!job) return;

			job.status = "pending";
			job.progress = 0;
			job.message = "Retrying download...";
			job.error = "";
		}
	}
});

const downloadJobsApi = queries.injectEndpoints({
	endpoints: (builder) => ({
		createDownloadReportJob: builder.mutation<ICreateReportJobResponseData, ICreateReportJobRequestData>({
			query: (requestData) => ({
				url: "/reports/create",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		})
	})
});

export const {
	addDownloadJob,
	updateDownloadJobStatus,
	removeDownloadJob,
	clearCompletedJobs,
	retryDownloadJob
} = downloadJobsReducer.actions;

export const { useCreateDownloadReportJobMutation } = downloadJobsApi;

export default downloadJobsReducer;

